import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import { postsService } from '../../services/posts.service';
import { pollsService, POLLS_CHANGED_EVENT } from '../../services/polls.service';
import { Post, Poll } from '../../types';
import { useAuth } from '../../context/AuthContext';
import { usePosts } from '../../context/PostsContext';
import PostCard from './PostCard';
import PollFeedCard from './PollFeedCard';
import CreatePostModal from './CreatePostModal';
import EditPostModal from './EditPostModal';
import {
  canCreatePosts,
  isAdminRole,
  isElevatedFeedRole,
  isRestrictedFeedRole,
} from '../../utils/roles';
import {
  isSidebarSpotlightPost,
  matchesBranchFeed,
  matchesDepartmentFeed,
  isScopedBranchOrDepartmentPost,
  isCreatedByDepartmentAdmin,
  pollMatchesFeed,
  shouldHideFromHomeForSameDepartmentViewer,
} from '../../utils/feedFilters';
import './PostFeed.css';

const PostFeed: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const feedParam = searchParams.get('feed');
  const feedMode: 'home' | 'branch' | 'department' =
    feedParam === 'department' ? 'department' : feedParam === 'branch' ? 'branch' : 'home';
  const { posts, isLoading, error: postsLoadError, refreshPosts } = usePosts();
  const [filteredPosts, setFilteredPosts] = useState<Post[]>([]);
  const [polls, setPolls] = useState<Poll[]>([]);
  const [activeTab, setActiveTab] = useState('GENERAL');
  const [activeCategory, setActiveCategory] = useState('ALL');
  const [sortBy, setSortBy] = useState('latest');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingPost, setEditingPost] = useState<Post | null>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);
  const tabsContainerRef = useRef<HTMLDivElement>(null);

  /** Home / elevated feeds: department tabs aligned with business departments (see Create Post “one department only”). */
  const departments = ['GENERAL', 'NEW', 'IT', 'HR', 'ADMIN', 'Finance', 'Sales', 'Service'];
  const categories = ['Posts', 'Announcements', 'Policy'];

  // Format department name: all letters uppercase
  const formatDepartmentName = (dept: string): string => {
    return dept.toUpperCase();
  };

  const canCreatePost = canCreatePosts(user?.role);
  /** Department tabs (GENERAL, NEW, HR, Admin, SpareParts, …) — show on Home for everyone; on other feeds keep for elevated roles only. */
  const showDepartmentTabs =
    (feedMode === 'home' || isElevatedFeedRole(user?.role)) &&
    !((feedMode === 'branch' || feedMode === 'department') && isAdminRole(user?.role));

  useEffect(() => {
    let cancelled = false;
    const loadPolls = async () => {
      try {
        const data = await pollsService.getAll();
        if (!cancelled) setPolls(data);
      } catch {
        if (!cancelled) setPolls([]);
      }
    };
    loadPolls();
    const interval = window.setInterval(loadPolls, 60_000);
    const onChanged = () => { loadPolls(); };
    window.addEventListener(POLLS_CHANGED_EVENT, onChanged);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
      window.removeEventListener(POLLS_CHANGED_EVENT, onChanged);
    };
  }, []);

  const handlePollUpdated = useCallback((updated: Poll) => {
    setPolls((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
  }, []);

  const handlePollDeleted = useCallback((pollId: string) => {
    setPolls((prev) => prev.filter((p) => p.id !== pollId));
  }, []);

  // Handle scrolling to a specific post when navigating from event
  useEffect(() => {
    const state = location.state as { scrollToPost?: string } | null;
    const scrollToPostId = state?.scrollToPost;
    if (scrollToPostId && filteredPosts.length > 0) {
      const timer = setTimeout(() => {
        const postElement = document.querySelector(`[data-post-id="${scrollToPostId}"]`);
        if (postElement) {
          postElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Highlight the post briefly
          postElement.classList.add('highlight-post');
          setTimeout(() => {
            postElement.classList.remove('highlight-post');
          }, 2000);
        }
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [filteredPosts, location]);

  const filterPosts = useCallback(() => {
    let filtered = posts.filter((p) => !isSidebarSpotlightPost(p));

    if (feedMode === 'branch') {
      filtered = filtered
        .filter((p) => matchesBranchFeed(p, user?.branch))
        .filter((p) => !isCreatedByDepartmentAdmin(p));
    } else if (feedMode === 'department') {
      filtered = filtered.filter((p) =>
        matchesDepartmentFeed(p, user?.branch, user?.department),
      );
    } else {
      // Home: scoped posts belong in My Branch / My Department only (not Home).
      filtered = filtered.filter((p) => !isScopedBranchOrDepartmentPost(p));
      // Same department as viewer: department posts / announcements / policies use My Department, not Home.
      filtered = filtered.filter(
        (p) => !shouldHideFromHomeForSameDepartmentViewer(p, user?.department),
      );
    }

    const applyDepartmentTabFilter =
      (feedMode === 'home' || !isRestrictedFeedRole(user?.role)) &&
      !((feedMode === 'branch' || feedMode === 'department') && isAdminRole(user?.role));
    if (applyDepartmentTabFilter) {
      if (activeTab === 'GENERAL') {
        // all in current feed slice
      } else if (activeTab === 'NEW') {
        const oneWeekAgo = new Date();
        oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
        filtered = filtered.filter((post) => new Date(post.createdAt) >= oneWeekAgo);
      } else {
        filtered = filtered.filter((post) => {
          const postDept =
            post.department?.toUpperCase() || post.createdBy?.department?.toUpperCase();
          return postDept === activeTab.toUpperCase();
        });
      }
    }

    if (activeCategory !== 'ALL') {
      const categoryMap: Record<string, string[]> = {
        Posts: ['POST', 'NEWS'],
        Announcements: ['ANNOUNCEMENT'],
        Policy: ['POLICY'],
      };
      filtered = filtered.filter((post) =>
        categoryMap[activeCategory]?.includes(post.category),
      );
    }

    filtered.sort((a, b) => {
      const dateA = new Date(a.createdAt).getTime();
      const dateB = new Date(b.createdAt).getTime();
      return sortBy === 'latest' ? dateB - dateA : dateA - dateB;
    });

    setFilteredPosts(filtered);
  }, [posts, feedMode, user?.branch, user?.department, user?.role, activeTab, activeCategory, sortBy]);

  useEffect(() => {
    filterPosts();
  }, [filterPosts]);

  const filteredPolls = useMemo(() => {
    const applyDepartmentTabFilter =
      (feedMode === 'home' || !isRestrictedFeedRole(user?.role)) &&
      !((feedMode === 'branch' || feedMode === 'department') && isAdminRole(user?.role));
    let list = polls.filter((p) =>
      pollMatchesFeed(p, feedMode, user?.branch, user?.department),
    );
    if (applyDepartmentTabFilter) {
      if (activeTab === 'NEW') {
        const oneWeekAgo = new Date();
        oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
        list = list.filter((p) => new Date(p.createdAt) >= oneWeekAgo);
      } else if (activeTab !== 'GENERAL') {
        list = [];
      }
    }
    if (activeCategory !== 'ALL') {
      list = [];
    }
    return list;
  }, [
    polls,
    feedMode,
    user?.branch,
    user?.department,
    user?.role,
    activeTab,
    activeCategory,
  ]);

  type FeedTimelineItem =
    | { kind: 'post'; createdAt: string; post: Post }
    | { kind: 'poll'; createdAt: string; poll: Poll };

  const feedTimeline = useMemo(() => {
    const items: FeedTimelineItem[] = [
      ...filteredPosts.map((post) => ({
        kind: 'post' as const,
        createdAt: post.createdAt,
        post,
      })),
      ...filteredPolls.map((poll) => ({
        kind: 'poll' as const,
        createdAt: poll.createdAt,
        poll,
      })),
    ];
    items.sort((a, b) => {
      const ta = new Date(a.createdAt).getTime();
      const tb = new Date(b.createdAt).getTime();
      return sortBy === 'latest' ? tb - ta : ta - tb;
    });
    return items;
  }, [filteredPosts, filteredPolls, sortBy]);

  useEffect(() => {
    const timer = setTimeout(() => {
      checkScrollButtons();
    }, 100);
    return () => clearTimeout(timer);
  }, [posts, feedMode]);

  const handlePostCreated = useCallback(() => {
    refreshPosts({ silent: true });
  }, [refreshPosts]);

  const handleEditPost = useCallback((post: Post) => {
    setEditingPost(post);
    setIsEditModalOpen(true);
  }, []);

  const handleDeletePost = useCallback(
    async (postId: string) => {
      try {
        await postsService.delete(postId);
        refreshPosts({ silent: true });
      } catch (error) {
        console.error('Failed to delete post:', error);
      }
    },
    [refreshPosts]
  );

  const handlePostUpdated = useCallback(() => {
    refreshPosts({ silent: true });
  }, [refreshPosts]);

  const checkScrollButtons = () => {
    const container = tabsContainerRef.current;
    if (container) {
      setShowLeftArrow(container.scrollLeft > 0);
      setShowRightArrow(
        container.scrollLeft < container.scrollWidth - container.clientWidth - 1
      );
    }
  };

  const scrollTabs = (direction: 'left' | 'right') => {
    const container = tabsContainerRef.current;
    if (container) {
      const scrollAmount = 200;
      container.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  useEffect(() => {
    // Set up scroll listeners
    const container = tabsContainerRef.current;
    if (container) {
      // Initial check
      checkScrollButtons();
      
      // Add event listeners
      container.addEventListener('scroll', checkScrollButtons);
      window.addEventListener('resize', checkScrollButtons);
      
      return () => {
        container.removeEventListener('scroll', checkScrollButtons);
        window.removeEventListener('resize', checkScrollButtons);
      };
    }
  }, [posts, feedMode]);

  return (
    <div className="post-feed flex border-2">
      {showDepartmentTabs && (
      <div className="department-tabs-container ">
        {showLeftArrow && (
          <button
            className="scroll-arrow scroll-arrow-left"
            onClick={() => scrollTabs('left')}
            aria-label="Scroll left"
          >
            ‹
          </button>
        )}
        <div
          className="department-tabs"
          ref={tabsContainerRef}
          onScroll={checkScrollButtons}
        >
          {departments.map((dept) => (
            <button
              key={dept}
              className={`dept-tab ${activeTab === dept ? 'active' : ''} ${dept.toLowerCase()}`}
              onClick={() => setActiveTab(dept)}
            >
              {formatDepartmentName(dept)}
            </button>
          ))}
        </div>
        {showRightArrow && (
          <button
            className="scroll-arrow scroll-arrow-right"
            onClick={() => scrollTabs('right')}
            aria-label="Scroll right"
          >
            ›
          </button>
        )}
      </div>
      )}

      {/* Category Filter & Sort */}
      <div className="filter-row">
        <div className="feed-scope-hint" aria-live="polite">
          {feedMode === 'home' && 'Home'}
          {feedMode === 'branch' && (
            <>
              <span className="feed-scope-label">My Branch</span>
              {user?.branch?.trim() ? (
                <span className="feed-scope-names">
                  {' · '}
                  <span className="feed-scope-value">{user.branch.trim()}</span>
                </span>
              ) : (
                <span className="feed-scope-missing"> · Branch not set on profile</span>
              )}
            </>
          )}
          {feedMode === 'department' && (
            <>
              <span className="feed-scope-label">My Department</span>
              <span className="feed-scope-names">
                {user?.department?.trim() ? (
                  <>
                    {' · '}
                    <span className="feed-scope-value">{user.department.trim()}</span>
                  </>
                ) : (
                  <span className="feed-scope-missing"> · Department not set</span>
                )}
              </span>
            </>
          )}
        </div>
        <div className="category-filter">
          <span className="filter-label">Filter:</span>
          {categories.map((cat) => (
            <button
              key={cat}
              className={`category-btn ${activeCategory === cat ? 'active' : ''}`}
              onClick={() => setActiveCategory(activeCategory === cat ? 'ALL' : cat)}
            >
              {cat}
            </button>
          ))}
        </div>

        <div className="sort-section">
          <span className="sort-label">Sort by:</span>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="sort-select"
          >
            <option value="latest">Latest</option>
            <option value="oldest">Oldest</option>
          </select>
        </div>
      </div>

      {/* Create post / poll (same roles as backend: not employees) */}
      {canCreatePost && (
        <div className="create-feed-actions">
          <button
            type="button"
            className="create-post-btn"
            onClick={() => setIsModalOpen(true)}
          >
            + Create Post
          </button>
          <button
            type="button"
            className="create-poll-btn"
            onClick={() => navigate('/polls')}
          >
            + Create poll
          </button>
        </div>
      )}

      {/* Posts + polls timeline */}
      <div className="posts-list">
        <div className="feed-onchilla-float">
          <video
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            aria-hidden
            ref={(el) => {
              if (el) {
                el.defaultMuted = true;
                el.play().catch(() => {});
              }
            }}
          >
            <source src="/onchilla.mp4" type="video/mp4" />
          </video>
        </div>
        {isLoading ? (
          <div className="loading">Loading posts...</div>
        ) : postsLoadError ? (
          <div className="feed-error" role="alert">
            <p>{postsLoadError}</p>
            <button type="button" className="feed-retry-btn" onClick={() => refreshPosts()}>
              Retry
            </button>
          </div>
        ) : feedTimeline.length === 0 ? (
          <div className="no-posts">No posts or polls found</div>
        ) : (
          feedTimeline.map((item) =>
            item.kind === 'post' ? (
              <div key={`post-${item.post.id}`} data-post-id={item.post.id}>
                <PostCard
                  post={item.post}
                  onEdit={handleEditPost}
                  onDelete={handleDeletePost}
                />
              </div>
            ) : (
              <div key={`poll-${item.poll.id}`} data-poll-id={item.poll.id}>
                <PollFeedCard
                  poll={item.poll}
                  onUpdated={handlePollUpdated}
                  onDeleted={handlePollDeleted}
                />
              </div>
            ),
          )
        )}
      </div>

      {/* Create Post Modal */}
      <CreatePostModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onPostCreated={handlePostCreated}
      />

      {/* Edit Post Modal */}
      <EditPostModal
        isOpen={isEditModalOpen}
        post={editingPost}
        onClose={() => {
          setIsEditModalOpen(false);
          setEditingPost(null);
        }}
        onPostUpdated={handlePostUpdated}
      />
    </div>
  );
};

export default PostFeed;
